import "@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions.css";
import { useI18nContext } from "@i18n/i18n-react";
import { cn } from "@shadcn/lib/utils";
import "mapbox-gl/dist/mapbox-gl.css";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { MapRef, ViewState, ViewStateChangeEvent } from "react-map-gl/mapbox";
import Map, { Marker } from "react-map-gl/mapbox";
import { HubListItemWithDistance } from "./hubFilters";

type MapViewProps = {
	hubs: readonly HubListItemWithDistance[];
	locationStatus?: "idle" | "pending" | "granted" | "denied";
	currentLocation?: {
		latitude: number;
		longitude: number;
	} | null;
	onCurrentLocationChange?: (location: { latitude: number; longitude: number }) => void;
	showGeolocateButton?: boolean;
} & (
	| {
			selectedHubId: string | undefined;
			setSelectedHubId: (hubId: string) => void;
	  }
	| { selectedHubId?: never; setSelectedHubId?: never }
);

export type MapViewHandle = {
	centerCurrentLocation: () => void;
};

const hasFiniteCoordinates = (hub: HubListItemWithDistance) => Number.isFinite(hub.meta.lat) && Number.isFinite(hub.meta.lng);

export const MapView = forwardRef<MapViewHandle, MapViewProps>(function MapView(props, ref) {
	const i18n = useI18nContext().LL["new-ui"];
	const { hubs, selectedHubId, setSelectedHubId, currentLocation, locationStatus, onCurrentLocationChange } = props;
	const mapRef = useRef<MapRef>(null);
	const [isLocating, setIsLocating] = useState(false);

	const [viewState, setViewState] = useState<Partial<ViewState>>(() => {
		if (currentLocation) {
			return { longitude: currentLocation.longitude, latitude: currentLocation.latitude, zoom: 15 };
		}
		const firstHub = hubs.find(hasFiniteCoordinates);
		if (firstHub) {
			return { longitude: firstHub.meta.lng, latitude: firstHub.meta.lat, zoom: 12 };
		}
		return { longitude: 10.45, latitude: 51.17, zoom: 6 };
	});

	const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN;

	if (!mapboxToken) {
		return (
			<div className="w-full h-full flex items-center justify-center bg-muted">
				<p className="text-muted-foreground">{i18n.MAPBOX_TOKEN_NOT_CONFIGURED()}</p>
			</div>
		);
	}

	const flyToLocation = useCallback((longitude: number, latitude: number) => {
		setViewState((current) => ({
			...current,
			longitude,
			latitude,
			zoom: 15,
			bearing: 0,
			pitch: 0,
		}));

		mapRef.current?.flyTo({
			center: [longitude, latitude],
			zoom: 15,
			bearing: 0,
			pitch: 0,
			duration: 1000,
		});
	}, []);

	const handleGeolocate = useCallback(
		(location?: { latitude: number; longitude: number }) => {
			if (location) {
				flyToLocation(location.longitude, location.latitude);
				return;
			}

			if (currentLocation) {
				flyToLocation(currentLocation.longitude, currentLocation.latitude);
			}

			if (!navigator.geolocation || isLocating) return;

			setIsLocating(true);
			navigator.geolocation.getCurrentPosition(
				({ coords }) => {
					const nextLocation = { latitude: coords.latitude, longitude: coords.longitude };
					onCurrentLocationChange?.(nextLocation);
					flyToLocation(coords.longitude, coords.latitude);
					setIsLocating(false);
				},
				() => {
					setIsLocating(false);
				},
				{ enableHighAccuracy: true, timeout: 10000 }
			);
		},
		[currentLocation, flyToLocation, isLocating, onCurrentLocationChange]
	);

	useEffect(() => {
		const selectedHub = hubs.find((hub) => hub.id === selectedHubId);
		if (!selectedHub || !hasFiniteCoordinates(selectedHub)) return;

		flyToLocation(selectedHub.meta.lng, selectedHub.meta.lat);
	}, [flyToLocation, selectedHubId, hubs]);

	useImperativeHandle(
		ref,
		() => ({
			centerCurrentLocation: () => handleGeolocate(),
		}),
		[handleGeolocate]
	);

	return (
		<div className="relative h-full w-full">
			<Map
				ref={mapRef}
				{...viewState}
				onMove={(evt: ViewStateChangeEvent) => setViewState(evt.viewState)}
				style={{ width: "100%", height: "100%" }}
				mapStyle="mapbox://styles/mapbox/streets-v12"
				mapboxAccessToken={mapboxToken}
			>
				{currentLocation ? (
					<Marker longitude={currentLocation.longitude} latitude={currentLocation.latitude} anchor="center">
						<div className="size-6 rounded-full border-3 border-white bg-blue-500 shadow-sm">
							<div className="h-full w-full rounded-full bg-blue-400/50" />
						</div>
					</Marker>
				) : null}

				{hubs.filter(hasFiniteCoordinates).map((hub) => {
					return (
						<Marker
							key={hub.id}
							longitude={hub.meta.lng}
							latitude={hub.meta.lat}
							anchor="center"
							onClick={(e) => {
								mapRef.current?.flyTo({
									center: [hub.meta.lng, hub.meta.lat],
									zoom: 15,
									duration: 1000,
								});
								setSelectedHubId?.(hub.id);
							}}
						>
							<div className="bg-black size-4 border-2 border-white rounded-full shadow-md" data-hub-id={hub.id}>
								<div
									className={cn("h-full w-full bg-black/50 rounded-full", {
										"animate-ping": hub.id === selectedHubId,
									})}
								></div>
							</div>
						</Marker>
					);
				})}
			</Map>
		</div>
	);
});
